import { Link } from "react-router-dom";
import { AlertTriangle, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useOverLimitRisks } from "@/hooks/useDowngradeRisk";
import { useState, useEffect } from "react";
import { useSubscription } from "@/hooks/useSubscription";
import { TIER_LABELS } from "@/lib/tiers";
import { usePlatformSettings } from "@/hooks/usePlatformSettings";

const DISMISS_KEY = "upbrief.overlimit-banner-dismissed";

/**
 * App-wide banner shown when a user's current usage exceeds their plan's
 * limits — typically after a downgrade. Persistent dismissal per-tier so
 * downgrading again re-surfaces it.
 */
export function OverLimitBanner() {
  const { tier } = useSubscription();
  const { pricingEnabled } = usePlatformSettings();
  const { risks, loading } = useOverLimitRisks();
  const [dismissed, setDismissed] = useState(false);

  // Reset dismissal when tier changes (so a new downgrade re-shows the banner)
  useEffect(() => {
    const stored = sessionStorage.getItem(DISMISS_KEY);
    setDismissed(stored === tier);
  }, [tier]);

  if (!pricingEnabled || loading || risks.length === 0 || dismissed) return null;

  const handleDismiss = () => {
    sessionStorage.setItem(DISMISS_KEY, tier);
    setDismissed(true);
  };

  const summary = risks.length === 1
    ? `Your ${risks[0].label.toLowerCase()} usage exceeds your ${TIER_LABELS[tier]} plan.`
    : `${risks.length} items exceed your ${TIER_LABELS[tier]} plan limits.`;

  return (
    <div className="border-b border-primary/20 bg-primary/5">
      <div className="px-4 sm:px-6 py-2.5 flex items-center gap-3">
        <AlertTriangle className="w-4 h-4 text-primary flex-shrink-0" />
        <p className="text-sm text-foreground flex-1 min-w-0">
          <span className="font-medium">{summary}</span>{" "}
          <span className="text-muted-foreground hidden sm:inline">
            Existing data is preserved, but new actions are limited.
          </span>
        </p>
        <Link to="/pricing">
          <Button size="sm" variant="default" className="h-7 text-xs">
            Upgrade
          </Button>
        </Link>
        <Link to="/settings/billing" className="hidden sm:block">
          <Button size="sm" variant="ghost" className="h-7 text-xs">
            Details
          </Button>
        </Link>
        <button
          onClick={handleDismiss}
          aria-label="Dismiss"
          className="text-muted-foreground hover:text-foreground transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
